import React, { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';

import PickerModal, {
  ListItem,
  PickerModalNoData,
  PickerModalSearch,
} from './pickerModalComponents';
import Spinner from '../../../shared/_TBComponents/Spinner';

import { ColorPrimary, ColorWhite } from '../../../shared/GlobalStyles';

import { journalTracker } from '../../../shared/utils/amplitudeHelper';
import { setCurrentGroup, setJournalTab, setSelectedAthlete } from '../ducks/journalActions';

const GroupsList = styled('div')`
  background-color: ${ColorWhite};
  color: ${ColorPrimary};
  margin: 8px auto 0 auto;
  padding: 2px 4px 2px 0;
  text-align: left;
  width: 90%;
`;

const GroupsContainer = styled('div')`
  height: 400px;
  margin-left: -4px;
  overflow-x: hidden;
  overflow-y: auto;
  padding-bottom: 20px;
  padding-left: 4px;
  width: 100%;
`;

const GroupItem = (props) => (
  <ListItem itemMarginLeft='12px' {...props} />
);

const GroupPickerModal = ({ onRequestClose, scrollToTop }) => {
  const {
    currentAthlete,
    groups,
    groupsError,
  } = useSelector((state) => state.journal.data);
  const groupsLoading = useSelector((state) => state.journal.ui.groupsLoading);
  const [searchString, setSearchString] = useState('');

  const dispatch = useDispatch();

  const filteredGroups = useMemo(
    () => (groups && Array.isArray(groups)
      ? groups.filter((group) => group.name.toLowerCase().includes(searchString.toLowerCase()))
      : []),
    [groups, searchString],
  );

  const groupsAvailable = !!filteredGroups.length && !groupsError;

  const handleSelectGroup = (group) => {
    if (currentAthlete?.id) {
      dispatch(setSelectedAthlete({}));
    }
    dispatch(setJournalTab('GROUPS'));
    dispatch(setCurrentGroup(group.id, group.name));
    onRequestClose();
    scrollToTop();
    journalTracker('Group picker modal', 'Group selected');
  };

  const renderNoData = () => {
    let message;
    if (groupsError) {
      message = groupsError?.message || groupsError?.response?.data?.message;
    } else if (searchString.length) {
      message = 'No groups found matching your search.';
    } else {
      message = 'No groups found.';
    }

    return <PickerModalNoData message={message} />;
  };

  const renderGroupsList = () => {
    if (groupsLoading) {
      return <Spinner large aria-label='Loading groups...' />;
    }

    if (!groupsAvailable) {
      return renderNoData();
    }

    return (
      <GroupsList>
        {filteredGroups.map((group) => (
          <GroupItem
            key={group.id}
            onClick={() => handleSelectGroup(group)}
            aria-label={`Select group: ${group.name}`}
          >
            <p>{group.name}</p>
          </GroupItem>
        ))}
      </GroupsList>
    );
  };

  return (
    <PickerModal onRequestClose={onRequestClose} headerText='Select a Group'>
      <PickerModalSearch
        placeholder='Search group by name'
        value={searchString}
        onChange={(e) => setSearchString(e.target.value)}
        ariaLabel='Search groups by name'
      />
      <GroupsContainer>
        {renderGroupsList()}
      </GroupsContainer>
    </PickerModal>
  );
};

GroupPickerModal.propTypes = {
  onRequestClose: PropTypes.func.isRequired,
  scrollToTop: PropTypes.func.isRequired,
};

export default GroupPickerModal;
